/**
 * FAQ API Hooks
 * Provides comprehensive FAQ document management with approval workflow
 * Utilizes FAQ-as-Document endpoints with simplified architecture
 *
 * Refactored: Phase 3.4 - Factory Pattern Migration
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 * Previous: 543 lines with 14 hooks and 14 helper functions
 * Current: Reduced by ~45% through factory pattern adoption
 */

import { QueryClient } from '@tanstack/react-query';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook } from './factories';
import {
  FAQDocumentCreate,
  FAQDocumentUpdate,
  FAQStatus,
  FAQStatusUpdate,
  FAQSortBy,
  ApprovalStatus,
  FAQListResponse,
  FAQDocumentResponse,
  FAQSearchResponse,
  FAQStatsResponse,
} from '../../api/data-contracts';

// ==================== Query Keys ====================

export const faqQueryKeys = {
  all: ['faq-documents'] as const,
  lists: () => [...faqQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...faqQueryKeys.lists(), filters] as const,
  details: () => [...faqQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...faqQueryKeys.details(), id] as const,
  search: (query: string) => [...faqQueryKeys.all, 'search', query] as const,
  stats: () => [...faqQueryKeys.all, 'stats'] as const,
  pending: () => [...faqQueryKeys.all, 'pending'] as const,
};

// ==================== Types ====================

export interface UseFAQsOptions extends Record<string, unknown> {
  status?: FAQStatus;
  approval_status?: ApprovalStatus;
  category?: string;
  sortBy?: FAQSortBy;
  page?: number;
  page_size?: number;
  enabled?: boolean;
  // Role-based filtering
  view?: 'all' | 'my' | 'pending' | 'approved' | 'rejected';
}

// ==================== Query Hooks ====================

/**
 * Hook zum Laden von FAQs mit Filterung
 */
export const useFAQs = createQueryHook<FAQListResponse, UseFAQsOptions>({
  queryKey: (options) => faqQueryKeys.list(options),
  queryFn: (apiClient, options) =>
    apiClient.listFaqsApiFaqDocumentsGet({
      status_filter: options.status,
      approval_status: options.approval_status,
      category: options.category,
      sort_by: options.sortBy,
      page: options.page || 1,
      page_size: options.page_size || 20,
    }).then(response => response.data),
  logContext: 'GET /api/faq-documents',
  staleTime: QUERY_STALE_TIME.SHORT, // 30 seconds for dynamic FAQ lists
  onSuccess: (data) => {
    log.api.response('GET', '/api/faq-documents', 200, { count: data.faqs.length });
  },
});

/**
 * Hook zum Laden eines einzelnen FAQ
 */
export const useFAQ = createQueryHook<FAQDocumentResponse, string>({
  queryKey: (faqId) => faqQueryKeys.detail(faqId),
  queryFn: (apiClient, faqId) =>
    apiClient.getFaqApiFaqDocumentsFaqIdGet(faqId).then(response => response.data),
  logContext: 'GET /api/faq-documents/:id',
  staleTime: QUERY_STALE_TIME.MEDIUM, // 1 minute for individual FAQs
});

/**
 * Hook zum Durchsuchen von FAQs
 */
export const useSearchFAQs = createQueryHook<
  FAQSearchResponse,
  { query: string; limit: number }
>({
  queryKey: ({ query }) => faqQueryKeys.search(query),
  queryFn: (apiClient, { query, limit }) =>
    apiClient.searchFaqsApiFaqDocumentsSearchGet({ query, limit }).then(response => response.data),
  logContext: 'GET /api/faq-documents/search',
  staleTime: QUERY_STALE_TIME.MEDIUM, // 1 minute for search results
  onSuccess: (data, params) => {
    log.api.response('GET', '/api/faq-documents/search', 200, {
      query: params.query,
      results: data.total_results,
    });
  },
});

/**
 * Hook zum Laden von FAQ Statistiken
 */
export const useFAQStats = createQueryHook<FAQStatsResponse, void>({
  queryKey: () => faqQueryKeys.stats(),
  queryFn: (apiClient) =>
    apiClient.getFaqStatsApiFaqDocumentsStatsGet().then(response => response.data),
  logContext: 'GET /api/faq-documents/stats',
  staleTime: QUERY_STALE_TIME.MEDIUM, // 1 minute for stats
  onSuccess: (data) => {
    log.api.response('GET', '/api/faq-documents/stats', 200, { total: data.total_faqs });
  },
});

// ==================== Mutation Hooks - CRUD Operations ====================

/**
 * Hook zum Erstellen eines FAQ
 */
export const useCreateFAQ = createMutationHook<FAQDocumentResponse, FAQDocumentCreate>({
  mutationFn: (apiClient, faqData) =>
    apiClient.createFaqApiFaqDocumentsPost(faqData).then(response => response.data),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'POST /api/faq-documents',
  logMethod: 'POST',
  onSuccess: (_data, variables) => {
    log.user.action('faq_created', 'useFAQs', { question: variables.question });
  },
});

/**
 * Hook zum Aktualisieren eines FAQ
 */
export const useUpdateFAQ = createMutationHook<
  FAQDocumentResponse,
  { faqId: string; updateData: FAQDocumentUpdate }
>({
  mutationFn: (apiClient, { faqId, updateData }) =>
    apiClient.updateFaqApiFaqDocumentsFaqIdPut(faqId, updateData).then(response => response.data),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'PUT /api/faq-documents/:id',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('faq_updated', 'useFAQs', { faqId: variables.faqId });
  },
});

/**
 * Hook zum Löschen eines FAQ
 */
export const useDeleteFAQ = createMutationHook<void, string>({
  mutationFn: (apiClient, faqId) =>
    apiClient.deleteFaqApiFaqDocumentsFaqIdDelete(faqId).then(() => undefined),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'DELETE /api/faq-documents/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, faqId) => {
    log.user.action('faq_deleted', 'useFAQs', { faqId });
  },
});

// ==================== Mutation Hooks - Approval Workflow ====================

/**
 * Hook zum Einreichen eines FAQ zur Genehmigung
 */
export const useSubmitFAQ = createMutationHook<FAQDocumentResponse, string>({
  mutationFn: (apiClient, faqId) =>
    apiClient.submitFaqForApprovalApiFaqDocumentsFaqIdSubmitPost(faqId).then(response => response.data),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'POST /api/faq-documents/:id/submit',
  logMethod: 'POST',
  onSuccess: (_data, faqId) => {
    log.user.action('faq_submitted', 'useFAQs', { faqId });
  },
});

/**
 * Hook zum Einreichen eines FAQ zur Genehmigung (neue Approval-API, alias)
 */
export const useSubmitFAQForApproval = useSubmitFAQ;

/**
 * Hook zum Genehmigen eines FAQ (für Moderatoren+)
 */
export const useApproveFAQ = createMutationHook<FAQDocumentResponse, string>({
  mutationFn: (apiClient, faqId) =>
    apiClient.approveFaqApiFaqDocumentsFaqIdApprovePost(faqId).then(response => response.data),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'POST /api/faq-documents/:id/approve',
  logMethod: 'POST',
  onSuccess: (_data, faqId) => {
    log.user.action('faq_approved', 'useFAQs', { faqId });
  },
});

/**
 * Hook zum Ablehnen eines FAQ (für Moderatoren+)
 */
export const useRejectFAQ = createMutationHook<
  FAQDocumentResponse,
  { faqId: string; reason: string }
>({
  mutationFn: (apiClient, { faqId, reason }) =>
    apiClient.rejectFaqApiFaqDocumentsFaqIdRejectPost(faqId, {
      rejection_reason: reason,
    }).then(response => response.data),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'POST /api/faq-documents/:id/reject',
  logMethod: 'POST',
  onSuccess: (_data, variables) => {
    log.user.action('faq_rejected', 'useFAQs', { faqId: variables.faqId });
  },
});

/**
 * Hook zum Review eines FAQ via Status Update (für Moderatoren+)
 * Unterstützt sowohl Genehmigung als auch Ablehnung
 */
export const useReviewFAQ = createMutationHook<
  FAQDocumentResponse,
  {
    faqId: string;
    reviewData: { action: string; rejection_reason?: string; auto_publish?: boolean };
  }
>({
  mutationFn: (apiClient, { faqId, reviewData }) => {
    // Map review action to status update
    const statusUpdate: FAQStatusUpdate = {
      status: reviewData.action === 'approve' ? FAQStatus.Approved : FAQStatus.Rejected,
      rejection_reason: reviewData.rejection_reason,
      auto_publish: reviewData.auto_publish || false,
    };

    return apiClient
      .updateFaqStatusApiFaqDocumentsFaqIdStatusPost(faqId, statusUpdate)
      .then(response => response.data);
  },
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'POST /api/faq-documents/:id/status',
  logMethod: 'POST',
  onSuccess: (_data, variables) => {
    log.user.action('faq_reviewed', 'useFAQs', {
      faqId: variables.faqId,
      action: variables.reviewData.action,
    });
  },
});

/**
 * Hook zum Zurückziehen eines FAQ aus dem Review-Prozess
 * Ändert Status von pending_review zurück zu draft
 */
export const useWithdrawFAQ = createMutationHook<
  FAQDocumentResponse,
  string
>({
  mutationFn: (apiClient, faqId) =>
    apiClient.updateFaqStatusApiFaqDocumentsFaqIdStatusPost(faqId, {
      status: FAQStatus.Draft,
    }).then(response => response.data),
  invalidateQueries: [
    faqQueryKeys.lists(),
    faqQueryKeys.stats(),
  ],
  logContext: 'POST /api/faq-documents/:id/status (withdraw)',
  logMethod: 'POST',
  onSuccess: (_data, faqId) => {
    log.user.action('faq_withdrawn', 'useFAQs', { faqId });
  },
});

/**
 * Hook zum Bewerten eines FAQ
 * Uses optimistic updates for instant UI feedback without refetching
 */
export const useRateFAQ = createMutationHook<FAQDocumentResponse, { faqId: string; helpful: boolean }>({
  mutationFn: (apiClient, { faqId, helpful }) =>
    apiClient.rateFaqApiFaqDocumentsFaqIdRatePost(faqId, { helpful }).then(response => response.data),
  logContext: 'POST /api/faq-documents/:id/rate',
  logMethod: 'POST',
  onMutate: async ({ faqId, helpful }, queryClient) => {
    // Cancel any outgoing refetches to avoid race conditions
    await queryClient.cancelQueries({ queryKey: faqQueryKeys.lists() });
    await queryClient.cancelQueries({ queryKey: faqQueryKeys.detail(faqId) });

    // Snapshot the previous values for rollback
    const previousQueries = new Map<string, unknown>();

    // Get all FAQ list queries from cache
    const queries = queryClient.getQueriesData({ queryKey: faqQueryKeys.lists() });

    // Update each cached query
    queries.forEach(([queryKey, oldData]) => {
      if (!oldData) {return;}

      // Store previous data for rollback
      previousQueries.set(JSON.stringify(queryKey), oldData);

      // Optimistically update the FAQ in the cache
      queryClient.setQueryData(queryKey, (old: FAQListResponse | undefined) => {
        if (!old) {return old;}

        return {
          ...old,
          faqs: old.faqs.map((faq) => {
            if (faq.document_id !== faqId) {return faq;}

            // Calculate new counts based on previous vote
            let newHelpfulCount = faq.helpful_count || 0;
            let newNotHelpfulCount = faq.not_helpful_count || 0;
            const previousVote = faq.user_vote;

            // Remove previous vote counts
            if (previousVote === 'helpful') {
              newHelpfulCount = Math.max(0, newHelpfulCount - 1);
            } else if (previousVote === 'not_helpful') {
              newNotHelpfulCount = Math.max(0, newNotHelpfulCount - 1);
            }

            // Add new vote counts
            if (helpful) {
              newHelpfulCount += 1;
            } else {
              newNotHelpfulCount += 1;
            }

            return {
              ...faq,
              helpful_count: newHelpfulCount,
              not_helpful_count: newNotHelpfulCount,
              user_vote: helpful ? 'helpful' : 'not_helpful',
            };
          }),
        };
      });
    });

    // Also update the detail cache for the specific FAQ
    const detailKey = faqQueryKeys.detail(faqId);
    const previousDetail = queryClient.getQueryData(detailKey);
    if (previousDetail) {
      previousQueries.set(JSON.stringify(detailKey), previousDetail);

      queryClient.setQueryData(detailKey, (old: FAQDocumentResponse | undefined) => {
        if (!old) {return old;}

        // Calculate new counts based on previous vote
        let newHelpfulCount = old.helpful_count || 0;
        let newNotHelpfulCount = old.not_helpful_count || 0;
        const previousVote = old.user_vote;

        // Remove previous vote counts
        if (previousVote === 'helpful') {
          newHelpfulCount = Math.max(0, newHelpfulCount - 1);
        } else if (previousVote === 'not_helpful') {
          newNotHelpfulCount = Math.max(0, newNotHelpfulCount - 1);
        }

        // Add new vote counts
        if (helpful) {
          newHelpfulCount += 1;
        } else {
          newNotHelpfulCount += 1;
        }

        return {
          ...old,
          helpful_count: newHelpfulCount,
          not_helpful_count: newNotHelpfulCount,
          user_vote: helpful ? 'helpful' : 'not_helpful',
        };
      });
    }

    return { previousQueries, queryClient };
  },
  onError: (_error, _variables, context) => {
    // Rollback optimistic updates on error
    if (context && typeof context === 'object' && 'previousQueries' in context && 'queryClient' in context) {
      const { previousQueries, queryClient: qc } = context as {
        previousQueries: Map<string, unknown>;
        queryClient: QueryClient;
      };

      previousQueries.forEach((data, queryKeyString) => {
        const queryKey = JSON.parse(queryKeyString);
        qc.setQueryData(queryKey, data);
      });

      log.api.error('POST', '/api/faq-documents/:id/rate', new Error('Failed to rate FAQ, rolled back'));
    }
  },
  onSuccess: (_data, variables) => {
    log.user.action('faq_rated', 'useFAQs', {
      faqId: variables.faqId,
      helpful: variables.helpful
    });
  },
});

/**
 * Hook zum Entfernen einer FAQ-Bewertung
 * Uses optimistic updates for instant UI feedback without refetching
 */
export const useUnrateFAQ = createMutationHook<FAQDocumentResponse, string>({
  mutationFn: (apiClient, faqId) =>
    apiClient.unrateFaqApiFaqDocumentsFaqIdRateDelete(faqId).then(response => response.data),
  logContext: 'DELETE /api/faq-documents/:id/rate',
  logMethod: 'DELETE',
  onMutate: async (faqId, queryClient) => {
    // Cancel any outgoing refetches to avoid race conditions
    await queryClient.cancelQueries({ queryKey: faqQueryKeys.lists() });
    await queryClient.cancelQueries({ queryKey: faqQueryKeys.detail(faqId) });

    // Snapshot the previous values for rollback
    const previousQueries = new Map<string, unknown>();

    // Get all FAQ list queries from cache
    const queries = queryClient.getQueriesData({ queryKey: faqQueryKeys.lists() });

    // Update each cached query
    queries.forEach(([queryKey, oldData]) => {
      if (!oldData) {return;}

      // Store previous data for rollback
      previousQueries.set(JSON.stringify(queryKey), oldData);

      // Optimistically update the FAQ in the cache
      queryClient.setQueryData(queryKey, (old: FAQListResponse | undefined) => {
        if (!old) {return old;}

        return {
          ...old,
          faqs: old.faqs.map((faq) => {
            if (faq.document_id !== faqId) {return faq;}

            // Calculate new counts by removing the vote
            let newHelpfulCount = faq.helpful_count || 0;
            let newNotHelpfulCount = faq.not_helpful_count || 0;
            const previousVote = faq.user_vote;

            // Remove previous vote counts
            if (previousVote === 'helpful') {
              newHelpfulCount = Math.max(0, newHelpfulCount - 1);
            } else if (previousVote === 'not_helpful') {
              newNotHelpfulCount = Math.max(0, newNotHelpfulCount - 1);
            }

            return {
              ...faq,
              helpful_count: newHelpfulCount,
              not_helpful_count: newNotHelpfulCount,
              user_vote: null,
            };
          }),
        };
      });
    });

    // Also update the detail cache for the specific FAQ
    const detailKey = faqQueryKeys.detail(faqId);
    const previousDetail = queryClient.getQueryData(detailKey);
    if (previousDetail) {
      previousQueries.set(JSON.stringify(detailKey), previousDetail);

      queryClient.setQueryData(detailKey, (old: FAQDocumentResponse | undefined) => {
        if (!old) {return old;}

        // Calculate new counts by removing the vote
        let newHelpfulCount = old.helpful_count || 0;
        let newNotHelpfulCount = old.not_helpful_count || 0;
        const previousVote = old.user_vote;

        // Remove previous vote counts
        if (previousVote === 'helpful') {
          newHelpfulCount = Math.max(0, newHelpfulCount - 1);
        } else if (previousVote === 'not_helpful') {
          newNotHelpfulCount = Math.max(0, newNotHelpfulCount - 1);
        }

        return {
          ...old,
          helpful_count: newHelpfulCount,
          not_helpful_count: newNotHelpfulCount,
          user_vote: null,
        };
      });
    }

    return { previousQueries, queryClient };
  },
  onError: (_error, _variables, context) => {
    // Rollback optimistic updates on error
    if (context && typeof context === 'object' && 'previousQueries' in context && 'queryClient' in context) {
      const { previousQueries, queryClient: qc } = context as {
        previousQueries: Map<string, unknown>;
        queryClient: QueryClient;
      };

      previousQueries.forEach((data, queryKeyString) => {
        const queryKey = JSON.parse(queryKeyString);
        qc.setQueryData(queryKey, data);
      });

      log.api.error('DELETE', '/api/faq-documents/:id/rate', new Error('Failed to unrate FAQ, rolled back'));
    }
  },
  onSuccess: (_data, faqId) => {
    log.user.action('faq_unrated', 'useFAQs', { faqId });
  },
});

// ==================== Convenience Hooks ====================

/**
 * Hook für rollenbasierte FAQ-Listen
 * Mappt View-Namen auf entsprechende Filter
 */
export const useFAQsByView = (
  view: 'all' | 'my' | 'pending' | 'approved' | 'rejected' = 'all'
) => {
  const options: UseFAQsOptions = { view };

  switch (view) {
    case 'pending':
      options.approval_status = ApprovalStatus.PendingReview;
      break;
    case 'approved':
      options.approval_status = ApprovalStatus.Approved;
      break;
    case 'rejected':
      options.approval_status = ApprovalStatus.Rejected;
      break;
    case 'my':
      // Backend should filter by current user automatically
      break;
    case 'all':
    default:
      // No additional filters
      break;
  }

  return useFAQs(options, { enabled: options.enabled });
};

/**
 * Hook für Moderation Dashboard - FAQs die auf Genehmigung warten
 */
export const usePendingApprovalFAQs = (options: { page?: number; page_size?: number } = {}) => {
  return useFAQs(
    {
      approval_status: ApprovalStatus.PendingReview,
      page: options.page || 1,
      page_size: options.page_size || 20,
    },
    { enabled: true }
  );
};
